import {
  BACKGROUND_MUSIC_TRACKS,
  MATCH_MUSIC_GAP_MS,
  MATCH_MUSIC_ROUND_BLOCKS,
  MATCH_MUSIC_THEME_REPEATS,
  TUTORIAL_MUSIC_BLOCK,
} from '../../configs/backgroundMusic';

// Ключ программы музыки партии (см. createBackgroundMusicPlayer): смена блока
// раундов — та же программа, звучащий трек не обрывается
export const MATCH_MUSIC_PROGRAM_KEY = 'match';

/**
 * Блок раундов, музыка которого звучит сейчас (см. MATCH_MUSIC_ROUND_BLOCKS)
 * @param {Object} params
 * @param {number} params.round - текущий раунд партии (0 — расстановка)
 * @param {boolean} [params.isTutorial] - идет глава обучения
 * @param {Array} [params.blocks]
 * @param {Object} [params.tutorialBlock]
 */
export const getMatchMusicBlock = ({
  round,
  isTutorial = false,
  blocks = MATCH_MUSIC_ROUND_BLOCKS,
  tutorialBlock = TUTORIAL_MUSIC_BLOCK,
}) => {
  if (isTutorial) {
    return tutorialBlock;
  }
  const safeRound = Number.isFinite(round) ? round : 0;
  return blocks.reduce((current, block) => (safeRound >= block.fromRound ? block : current), blocks[0]);
};

// Случайный трек блока. Прошлый трек блока повторяется, только если других нет
const pickBlockTrack = (block, lastBlockTrack, random) => {
  const candidates = block.tracks.length > 1
    ? block.tracks.filter((track) => track !== lastBlockTrack)
    : block.tracks;
  const index = Math.min(candidates.length - 1, Math.floor(random() * candidates.length));
  return candidates[index];
};

// После previous звучит тема: после трека блока — если блок тот же, после
// темы — если блок тот же и повторов было меньше themeRepeats
const checkIfThemeNext = (previous, block, themeRepeats) => {
  if (!previous || themeRepeats < 1) {
    return false;
  }
  if (previous.blockId !== null) {
    return previous.blockId === block.id;
  }
  return previous.themeBlockId === block.id && previous.themePlays < themeRepeats;
};

/**
 * Что звучит в партии следующим. Трек блока раундов и тема партии чередуются:
 *  - начало партии — трек текущего блока;
 *  - после трека блока — тема, а если за это время начался другой блок —
 *    сразу трек нового блока;
 *  - тема звучит themeRepeats раз подряд, затем — трек текущего блока; начался
 *    другой блок — трек нового блока сразу после текущего повтора.
 *
 * @param {Object} params
 * @param {Object|null} params.previous - что звучало перед этим (то, что
 *   отдал прошлый вызов), null — начало
 * @param {{id: string, tracks: Array}} params.block - текущий блок раундов
 * @param {{src: string, volume: number}} [params.theme] - тема партии
 * @param {number} [params.themeRepeats] - сколько раз подряд звучит тема
 * @param {Function} [params.random] - () => [0, 1)
 * @returns {{track: Object, blockId: string|null, lastBlockTrack: Object|null}}
 *   blockId — блок трека (null у темы), lastBlockTrack — последний звучавший
 *   трек блока: по нему следующий выбор не повторяет трек. У темы еще
 *   themeBlockId (блок, после трека которого она началась) и themePlays
 *   (который это повтор, с 1)
 */
export const pickNextMatchMusicItem = ({
  previous,
  block,
  theme = BACKGROUND_MUSIC_TRACKS.GAME,
  themeRepeats = MATCH_MUSIC_THEME_REPEATS,
  random = Math.random,
}) => {
  const lastBlockTrack = previous ? previous.lastBlockTrack : null;

  if (checkIfThemeNext(previous, block, themeRepeats)) {
    const isRepeat = previous.blockId === null;
    return {
      track: theme,
      blockId: null,
      themeBlockId: isRepeat ? previous.themeBlockId : previous.blockId,
      themePlays: isRepeat ? previous.themePlays + 1 : 1,
      lastBlockTrack,
    };
  }

  const track = pickBlockTrack(block, lastBlockTrack, random);
  return {track, blockId: block.id, lastBlockTrack: track};
};

/**
 * Пауза после доигравшего трека партии: повтор темы следует за темой без
 * паузы (как трек по кругу), остальные переходы — с паузой gapMs
 * @param {Object} params
 * @param {Object} params.finished - доигравший элемент (из pickNextMatchMusicItem)
 * @param {{id: string}} params.block - текущий блок раундов
 * @param {number} [params.themeRepeats]
 * @param {number} [params.gapMs]
 */
export const getMatchMusicGapMs = ({
  finished,
  block,
  themeRepeats = MATCH_MUSIC_THEME_REPEATS,
  gapMs = MATCH_MUSIC_GAP_MS,
}) => {
  const isThemeRepeatNext = Boolean(finished) && finished.blockId === null
    && checkIfThemeNext(finished, block, themeRepeats);
  return isThemeRepeatNext ? 0 : gapMs;
};

// Программа на каждый блок одна и та же: объект программы не меняется, пока
// блок тот же, и эффект в BackgroundMusic не дергает проигрыватель без нужды
const programsByBlock = new Map();

/**
 * Программа музыки партии для проигрывателя (createBackgroundMusicPlayer):
 * чередование треков блока и темы с паузой между треками. У всех блоков
 * один ключ — смена блока проигрыватель не перезапускает, а лишь меняет
 * правило выбора следующего трека
 * @param {{id: string, tracks: Array}} block
 */
export const getMatchMusicProgram = (block) => {
  if (!programsByBlock.has(block)) {
    programsByBlock.set(block, Object.freeze({
      key: MATCH_MUSIC_PROGRAM_KEY,
      getGapMs: (finished) => getMatchMusicGapMs({finished, block}),
      getNextItem: (previous) => pickNextMatchMusicItem({previous, block}),
    }));
  }
  return programsByBlock.get(block);
};
